import { useState, useEffect, useRef, useMemo } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import moment from "moment";
import { ReactCalendar } from "components";
import { BusIcon, SwapIcon, CalendarIcon, HistoryIcon } from "assets/icons";
import { useGetCityListQuery } from "../redux/services/cityServices";
import { resetBookingStates } from "../redux/reducers/bookingReducer";
import { getSearchedBuses } from "../redux/axios";

export default function SearchBuses({
  isHome = false,
  setLoading,
  setSearchResults,
  setTripDate,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const cityResponse = useGetCityListQuery();

  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [openFromModal, setOpenFromModal] = useState(false);
  const [openToModal, setOpenToModal] = useState(false);
  const [dateOfJourney, setDateOfJourney] = useState(new Date());
  const [openJourneyDate, setOpenJourneyDate] = useState(false);
  const [dateOfReturn, setDateOfReturn] = useState(new Date());
  const [openReturnDate, setOpenReturnDate] = useState(false);

  const journeyCalendarRef = useRef(null);
  const returnCalendarRef = useRef(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    let from_id = queryParams.get("from_id") || "";
    let from_city = queryParams.get("from_city") || "";
    let to_id = queryParams.get("to_id") || "";
    let to_city = queryParams.get("to_city") || "";
    let journey_date = queryParams.get("journey_date") || new Date();

    setFrom({
      id: from_id,
      title: from_city,
    });
    setTo({
      id: to_id,
      title: to_city,
    });
    setDateOfJourney(journey_date);
    setSearchFrom(from_city);
    setSearchTo(to_city);

    let params = {
      boarding_id: from_id,
      drop_id: to_id,
      trip_date: moment(journey_date).format("YYYY-MM-DD"),
      sort: 1,
      pickupfilter: 0,
      dropfilter: 0,
      bustype: 1,
      operatorlist: 1,
      facilitylist: [1, 2, 3, 4, 5, 6, 7, 8],
    };
    if (from_id || to_id) handleSearchAPI(params);
  }, [location.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        journeyCalendarRef.current &&
        !journeyCalendarRef.current.contains(event.target)
      ) {
        setOpenJourneyDate(false);
      }
      if (
        returnCalendarRef.current &&
        !returnCalendarRef.current.contains(event.target)
      ) {
        setOpenReturnDate(false);
      }
    };

    document.body.addEventListener("click", handleClickOutside);

    return () => {
      document.body.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const fromFilteredCities = useMemo(() => {
    if (cityResponse.data?.citylist?.length) {
      return cityResponse.data.citylist.filter((x) => {
        if (x) {
          return String(x?.title)
            .toLowerCase()
            .includes(searchFrom.toLowerCase());
        } else {
          return false;
        }
      });
    } else {
      return [];
    }
  }, [searchFrom, cityResponse.data?.citylist]);

  const toFilteredCities = useMemo(() => {
    if (cityResponse.data?.citylist?.length) {
      return cityResponse.data.citylist.filter((x) => {
        if (x) {
          return String(x?.title)
            .toLowerCase()
            .includes(searchTo.toLowerCase());
        } else {
          return false;
        }
      });
    } else {
      return [];
    }
  }, [searchTo, cityResponse.data?.citylist]);

  const recent = [
    // Your recent data here
  ];

  const handleFromOptionClick = (item) => {
    setFrom(item);
    setSearchFrom(item.title);
    setOpenFromModal(false);
  };

  const handleToOptionClick = (item) => {
    setTo(item);
    setSearchTo(item.title);
    setOpenToModal(false);
  };

  const handleSearchButton = () => {
    if (!from?.title) {
      toast.error("Please select from city");
      return;
    } else if (!to?.title) {
      toast.error("Please select to city");
      return;
    }

    if (isHome) {
      dispatch(resetBookingStates());
      window.scrollTo(0, 0);
      const params = new URLSearchParams({
        from_city: from?.title,
        from_id: from?.id,
        to_city: to?.title,
        to_id: to?.id,
        journey_date: moment(dateOfJourney).format("YYYY-MM-DD"),
      }).toString();
      navigate(`/book-tickets?${params}`);
    } else {
      let updateParams = {
        from_city: from?.title,
        from_id: from?.id,
        to_city: to?.title,
        to_id: to?.id,
        journey_date: moment(dateOfJourney).format("YYYY-MM-DD"),
      };
      const searchParams = new URLSearchParams(location.search);
      Object.keys(updateParams).forEach((key) => {
        searchParams.set(key, updateParams[key]);
      });
      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true }
      );
    }
  };

  const handleSearchAPI = async (params) => {
    let res = await getSearchedBuses(params, setLoading);
    setSearchResults(res?.BusData);
    setTripDate(params.trip_date);
  };

  return (
    <div className="w-full mb-[40px] md:mb-[60px] z-[2] -mt-[180px] lg:mt-0">
      <div className="bg-white shadow-block p-8 rounded-[16px] lg:rounded-none">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-6">
          <div className="w-full flex flex-col lg:flex-row items-center">
            <div className="w-full h-[52px] animation border-b-[1px] lg:border-[1px] border-[#79747E] hover:border-primary-700 lg:rounded-[4px] flex items-center gap-3 relative lg:px-4">
              <div className="text-sm text-[#1C1B1F] bg-white absolute top-[-10px] lg:left-2.5 px-0.5">
                From
              </div>
              <div className="text-primary-700">
                <BusIcon />
              </div>
              <input
                type="text"
                placeholder="Search From"
                value={searchFrom}
                onChange={(e) => setSearchFrom(e.target.value)}
                onClick={() => setOpenFromModal(true)}
                onFocus={() => setSearchFrom("")}
                onBlur={() => {
                  setSearchFrom(from?.title || "");
                  setTimeout(() => setOpenFromModal(false), 100);
                }}
                className="w-full h-full bg-transparent text-base text-[#1C1B1F]"
              />

              {/* options */}
              {openFromModal && (
                <div
                  className="w-full max-h-[620px] overflow-y-auto bg-white absolute top-[58px] left-0 shadow-hotCard border-[1px] border-black/25 px-5 py-4 flex flex-col gap-3 z-10"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {recent?.length ? (
                    <div className="flex flex-col">
                      <div className="text-[#263238] font-semibold mb-4">
                        Recent
                      </div>
                      {recent.map((item, index) => (
                        <div
                          key={index}
                          className="cursor-pointer flex items-center gap-4 py-3 border-b-[0.5px] border-[#C4C4C480] last:border-none"
                          onClick={() => handleFromOptionClick(item.label)}
                        >
                          <span className="text-primary-700">
                            <HistoryIcon />
                          </span>
                          <div className="text-sm font-medium text-[#263238]">
                            {item.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="flex flex-col">
                    <div className="text-[#263238] font-semibold mb-4">
                      Top Cities
                    </div>
                    {fromFilteredCities.map((item, index) => (
                      <div
                        key={index}
                        className="cursor-pointer flex items-center gap-4 py-3 border-b-[0.5px] border-[#C4C4C480] last:border-none"
                        onClick={() => handleFromOptionClick(item)}
                      >
                        <div className="text-sm font-medium text-[#263238]">
                          {item.title}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="min-h-8 min-w-8 bg-primary-700 text-white rounded-lg flex items-center justify-center lg:-ml-2 lg:-mr-2 mt-2.5 lg:mt-0 z-[2]">
              <SwapIcon />
            </div>
            <div className="w-full h-[52px] animation border-b-[1px] lg:border-[1px] border-[#79747E] hover:border-primary-700 lg:rounded-[4px] flex items-center gap-3 relative lg:px-4">
              <div className="text-sm text-[#1C1B1F] bg-white absolute top-[-10px] lg:left-2.5 px-0.5">
                To
              </div>
              <div className="text-primary-700">
                <BusIcon />
              </div>
              <input
                type="text"
                placeholder="To"
                value={searchTo}
                onChange={(e) => setSearchTo(e.target.value)}
                onClick={() => setOpenToModal(true)}
                onFocus={() => setSearchTo("")}
                onBlur={() => {
                  setSearchTo(to?.title || "");
                  setTimeout(() => setOpenToModal(false), 100);
                }}
                className="w-full h-full bg-transparent text-base text-[#1C1B1F]"
              />
              {/* options */}
              {openToModal && (
                <div
                  className="w-full max-h-[620px] overflow-y-auto bg-white absolute top-[58px] left-0 shadow-hotCard border-[1px] border-black/25 px-5 py-4 flex flex-col gap-3 z-10"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {recent?.length ? (
                    <div className="flex flex-col">
                      <div className="text-[#263238] font-semibold mb-4">
                        Recent
                      </div>
                      {recent.map((item, index) => (
                        <div
                          key={index}
                          className="cursor-pointer flex items-center gap-4 py-3 border-b-[0.5px] border-[#C4C4C480] last:border-none"
                          onClick={() => handleToOptionClick(item.label)}
                        >
                          <span className="text-primary-700">
                            <HistoryIcon />
                          </span>
                          <div className="text-sm font-medium text-[#263238]">
                            {item.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <div className="flex flex-col">
                    <div className="text-[#263238] font-semibold mb-4">
                      Top Cities
                    </div>
                    {toFilteredCities.map((item, index) => (
                      <div
                        key={index}
                        className="cursor-pointer flex items-center gap-4 py-3 border-b-[0.5px] border-[#C4C4C480] last:border-none"
                        onClick={() => handleToOptionClick(item)}
                      >
                        <div className="text-sm font-medium text-[#263238]">
                          {item.title}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="w-full flex flex-col lg:flex-row items-center gap-8 lg:gap-0">
            <div className="w-full flex flex-col relative">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenReturnDate(false);
                  setOpenJourneyDate(true);
                }}
                className={`w-full h-[52px] animation lg:rounded-[4px] lg:rounded-tr-none rounded-br-none flex items-center gap-3 relative lg:px-4 cursor-pointer border-b-[1px] lg:border-[1px] hover:border-primary-700 ${
                  openJourneyDate ? "border-primary-700" : "border-[#79747E]"
                }`}
              >
                <div className="text-[12px] lg:text-sm text-[#1C1B1F] bg-white absolute top-[-10px] lg:left-2.5 px-0.5">
                  Date of Journey
                </div>
                <div
                  className="text-primary-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  <CalendarIcon />
                </div>
                <div className="text-base text-[#1C1B1F]">
                  {moment(dateOfJourney).format("DD-MM-YYYY") || "Select Date"}
                </div>
              </div>
              {openJourneyDate && (
                <div
                  className="absolute top-[64px] z-10"
                  ref={journeyCalendarRef}
                >
                  <ReactCalendar
                    onChange={setDateOfJourney}
                    value={dateOfJourney}
                  />
                </div>
              )}
            </div>
            <div className="w-full flex flex-col relative">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenJourneyDate(false);
                  setOpenReturnDate(true);
                }}
                className={`w-full h-[52px] animation lg:rounded-[4px] lg:rounded-tl-none lg:rounded-bl-none flex items-center gap-3 relative lg:px-4 cursor-pointer border-none lg:border-solid lg:border-[1px] hover:border-primary-700 ${
                  openReturnDate ? "border-primary-700" : "border-[#79747E]"
                }`}
              >
                <div className="text-[12px] lg:text-sm text-[#1C1B1F] bg-white absolute top-[-10px] lg:left-2.5 px-0.5">
                  Date of Return (Optional)
                </div>
                <div
                  className="text-primary-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  <CalendarIcon />
                </div>
                <div className="text-base text-[#1C1B1F]">
                  {moment(dateOfReturn).format("DD-MM-YYYY") || "Select Date"}
                </div>
              </div>
              {openReturnDate && (
                <div
                  className="absolute top-[64px] z-10"
                  ref={returnCalendarRef}
                >
                  <ReactCalendar
                    onChange={setDateOfReturn}
                    value={dateOfReturn}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center">
            <button
              onClick={handleSearchButton}
              className="text-base font-semibold bg-primary-700 min-w-[180px] h-[48px] text-white rounded-[10px]"
            >
              Search Buses
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
